import { FC, useContext, useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../components/ui/FormFieldsContainer";
import { Button, Form, FormField, Input, ModalDialog, Select, useDebounce } from "@rothoblaas/design-system";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useInitializer from "../../hooks/useInitializer";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { useCalculationsApi } from "../../http/useCalculations";
import { useProjectsApi } from "../../http/useProjects";
import { PaginationQuery } from "../../types";

interface DuplicateCalculationDialogForm {
  project: {
    id: string;
    name: string;
  };
  name: string;
}

interface DuplicateCalculationDialogProps {
  calculation: {
    id: string;
    name: string;
    project: {
      id: string;
      name: string;
    } | null;
  };
  onClose: () => void;
  onSave: () => void;
  isAdmin?: boolean;
}

const DuplicateCalculationDialog: FC<DuplicateCalculationDialogProps> = ({
  calculation,
  onClose,
  onSave,
  isAdmin,
}: DuplicateCalculationDialogProps) => {
  const i18n = useI18n();
  const projectsApi = useProjectsApi();
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const calculationsApi = useCalculationsApi();

  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const projectsResponse = isAdmin
    ? projectsApi.useAdminProjects(query, aborter.current.signal)
    : projectsApi.useMyProjects(query, aborter.current.signal);
  const data = projectsResponse.data;
  const error = projectsResponse.error;
  const isInitialized = useInitializer(data || error);

  if (!data && !error && !isInitialized) {
    return;
  }

  const defaultProject = data?.items?.find(p => p.id === calculation.project?.id);
  const defaultValues: Partial<DuplicateCalculationDialogForm> = {
    project: defaultProject,
    name: calculation.name + ` (${i18n.translation.common.copy})`,
  };

  return (
    <ModalDialog onClose={() => onClose()} title={i18n.translation.calculations.duplicate}>
      <Form<DuplicateCalculationDialogForm>
        defaultValues={defaultValues}
        onSubmit={() => async (formData) => {
          return calculationsApi
            .duplicateCalculation(calculation.project?.id, calculation.id, {
              projectId: formData.project.id,
              name: formData.name,
            }, isAdmin)
            .then(() => onSave())
            .catch((err) => dispatch(showErrorNotification(i18n, err)));
        }}
        submitText={i18n.translation.common.ok}
        secondaryButton={
          <Button onClick={() => onClose()} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ register, formState: { errors }, control, reset }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [calculation.id]);

          return (
            <FormFieldsContainer fullWidth>
              <Controller
                name="project"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.selectProjectInwhichCreateCopy} error={fieldState.error}>
                    {({ labelId }) => (
                      <Select
                        {...field}
                        inputId={labelId}
                        options={data?.items ?? []} //data?.filter((x) => x.canEdit)
                        placeholder={`${i18n.translation.common.select}...`}
                        // isDisabled={(!initialProjectId && isLoadingProjects) || isSubmitting}
                        // isLoading={(!initialProjectId && isLoadingProjects) || isSubmitting}
                        getOptionValue={(x) => x.id}
                        getOptionLabel={(x) => x.name}
                        isInvalid={fieldState.invalid}
                        // onChange={(selectedOption) => setValue("project", selectedOption ?? null)}
                      />
                    )}
                  </FormField>
                )}
              />
              <FormField label={i18n.translation.calculations.newCalculationName} error={errors.name}>
                {({ labelId, isOptional, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    {...register("name", {
                      required: !isOptional,
                    })}
                  />
                )}
              </FormField>
            </FormFieldsContainer>
          );
        }}
      </Form>
    </ModalDialog>
  );
};

export default DuplicateCalculationDialog;
